import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { StatusCodes } from 'http-status-codes';

import { ApiEndpoints } from 'constants/endpointsEnum';

import { adfsLogoutSchema, authModesSchema, loginSchema } from 'schemas/api/auth';
import { apiResponseSchema } from 'schemas/api/common';

import { getBaseSalesURL } from 'services/api';
import axios, { createAxiosError, errorCatchOnHandler } from 'services/axios';
import { isDefined } from 'services/util/auxiliaryUtils';

// Requests made without a session name the platform and send Basic credentials. These go as Latin-1, as they always did.
const basicAuthHeaders = (credentials: string) => ({ Authorization: `Basic ${btoa(credentials)}`, 'X-Platform': 's2c' });

// The app's own credentials, for what a user asks before signing in: the sign-in mode and the password reset.
const recoveryHeaders = () => basicAuthHeaders(process.env.REACT_APP_RECOVEY_BASE64 ?? '');

/**
 * Sends the request `buildRequest` returns and resolves with what the reader for the answer's status takes from its
 * body. Any other answer, or none, is reported and rejected. The request is built inside the error handling, so a
 * failure to build it is reported too.
 */
const request = async <TResult>(
    tag: string,
    buildRequest: () => AxiosRequestConfig,
    readers: Partial<Record<number, (body: unknown) => TResult>>
): Promise<TResult> => {
    try {
        const requestCfg = buildRequest();
        const response = (await axios.request(requestCfg)) as AxiosResponse<unknown> | undefined;
        const read = response ? readers[response.status] : undefined;
        if (response && read) return read(response.data);
        return createAxiosError(requestCfg, response);
    } catch (error) {
        errorCatchOnHandler({ error, tag, fnc: 'apiAuthServices' });
        return Promise.reject(error);
    }
};

/** What happens before there is a session, or as it ends: the login, the sign-in mode, the password reset and the ADFS logout. */
export const apiAuthServices = {
    /** Signs in and resolves with the session token, or with `null` when the API refuses the credentials. */
    login: async (username: string, password: string, signal?: AbortSignal) =>
        request<string | null>(
            'login',
            () => ({
                url: `${getBaseSalesURL()}${ApiEndpoints.AUTH}`,
                method: 'GET',
                headers: basicAuthHeaders(`${username}:${password}`),
                signal,
            }),
            {
                [StatusCodes.OK]: (body) => loginSchema.parse(apiResponseSchema.parse(body).data).token,
                // Wrong credentials are an answer for the login form, not a failure to report.
                [StatusCodes.UNAUTHORIZED]: () => null,
                [StatusCodes.FORBIDDEN]: () => null,
            }
        ),

    /** How the user signs in (`login_by_sso`), or `null` for a username the API does not know. */
    getAuthModes: async (username: string, signal?: AbortSignal) =>
        request(
            'getAuthModes',
            () => ({
                url: `${getBaseSalesURL()}${ApiEndpoints.USER_AUTH_MODES}/${encodeURIComponent(username)}`,
                method: 'GET',
                headers: recoveryHeaders(),
                signal,
            }),
            {
                [StatusCodes.OK]: (body) => authModesSchema.parse(apiResponseSchema.parse(body).data),
                [StatusCodes.NO_CONTENT]: () => null,
            }
        ),

    /**
     * Asks for the password reset email. A 400 gets the same confirmation, as it always has: the screen does not tell
     * which emails have an account.
     */
    requestPasswordReset: async (email: string, signal?: AbortSignal) =>
        request(
            'requestPasswordReset',
            () => ({
                url: `${getBaseSalesURL()}${ApiEndpoints.USER_CREDENTIALS_EMAIL}/${encodeURIComponent(email)}`,
                method: 'GET',
                headers: recoveryHeaders(),
                signal,
            }),
            { [StatusCodes.OK]: () => undefined, [StatusCodes.BAD_REQUEST]: () => undefined }
        ),

    /**
     * Ends the ADFS session of `token`, named on the identity provider by its SAML ids, and resolves with the identity
     * provider's page that ends it there, or `null` without one.
     */
    logoutAdfs: async (token: string, nameId?: string, sessionIndex?: string, signal?: AbortSignal) =>
        request(
            'logoutAdfs',
            () => ({
                url: `${getBaseSalesURL()}${ApiEndpoints.AUTH_ADFS_LOGOUT}`,
                method: 'GET',
                params: isDefined(nameId) ? { nameid: nameId, sessionindex: sessionIndex } : undefined,
                headers: { Authorization: `JWT ${token}`, 'X-Platform': 's2c' },
                signal,
            }),
            { [StatusCodes.OK]: (body) => adfsLogoutSchema.parse(apiResponseSchema.parse(body).data) }
        ),
};
